import {
  StringMap,
  TFunctionResult,
  TOptions,
} from 'i18next'
import { useCallback } from 'react'
import {
  DefaultNamespace,
  KeyPrefix,
  Namespace,
  TFuncKey,
  TFuncReturn,
  // eslint-disable-next-line no-restricted-imports
  useTranslation as useTranslationCore,
  UseTranslationOptions,
  UseTranslationResponse as UseTranslationResponseCore,
} from 'react-i18next'

type TResultDanger<T> = {
  dangerouslySetInnerHTML: {
    __html: T
  }
}

interface TFunctionDanger<N extends Namespace = DefaultNamespace, TKPrefix = undefined> {
  <
    TKeys extends TFuncKey<N, TKPrefix> | TemplateStringsArray extends infer A ? A : never,
    TDefaultResult extends TFunctionResult = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string,
  ): TResultDanger<TFuncReturn<N, TKeys, TDefaultResult, TKPrefix>>
  <
    TKeys extends TFuncKey<N, TKPrefix> | TemplateStringsArray extends infer A ? A : never,
    TDefaultResult extends TFunctionResult = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string,
  ): TResultDanger<TFuncReturn<N, TKeys, TDefaultResult, TKPrefix>>
}

type UseTranslationResponse<N extends Namespace, TKPrefix = undefined> = UseTranslationResponseCore<N, TKPrefix> & {
  tDanger: TFunctionDanger
}

export const useTranslation = <N extends Namespace = DefaultNamespace, TKPrefix extends KeyPrefix<N> = undefined>(
  ns?: N | Readonly<N>,
  options?: UseTranslationOptions<TKPrefix>,
): UseTranslationResponse<N, TKPrefix> => {
  const base = useTranslationCore(ns, options) as UseTranslationResponse<N, TKPrefix>

  const tDanger = useCallback<TFunctionDanger<N, TKPrefix>>(
    (...args: Parameters<TFunctionDanger<N, TKPrefix>>): ReturnType<TFunctionDanger<N, TKPrefix>> => ({
      dangerouslySetInnerHTML: {
        __html: base.t(...args),
      },
    }),
    [base],
  )

  base.tDanger = tDanger

  return base
}
